import { createMemo } from "solid-js";
import { useParams } from "@solidjs/router";
import { useQuery } from "@tanstack/solid-query";
import { organization, useActiveMemberRole, useSession } from "../../lib/auth-client";

type ProjectAction = "create" | "list" | "update" | "delete" | "toggleSpotlight";
type OrgAction = "update" | "delete";
type RoleType = "owner" | "admin" | "member";

function checkProjectPermission(role: string | undefined, action: ProjectAction): boolean {
  if (!role) return false;
  const result = organization.checkRolePermission({
    role: role as RoleType,
    permissions: { project: [action] } as any,
  });
  return result === true;
}

function checkOrgPermission(role: string | undefined, action: OrgAction): boolean {
  if (!role) return false;
  const result = organization.checkRolePermission({
    role: role as RoleType,
    permissions: { organization: [action] },
  });
  return result === true;
}

/**
 * organizationSlug: the organization to check the role in, when it isn't the
 * one of the URL — a room page (/r/:roomId) belongs to the organization of the
 * room, whatever organization happens to be active.
 */
export function useProjectPermissions(options?: { organizationSlug?: () => string | null | undefined }) {
  const params = useParams();
  const organizationSlug = createMemo(() => options?.organizationSlug?.() || params.tenantId);
  const sessionQuery = useSession();
  const activeMemberRoleQuery = useActiveMemberRole();

  // On tenant pages, resolve the member role for the URL organization
  // directly. Deriving it from the *active* organization races the
  // setActive() switch TenantLayout performs on a full page load: the role
  // query can settle for the wrong (or no) organization first, and the
  // transient no-permissions state triggers the pages' redirect-to-home
  // fallback before the switch completes.
  const tenantRoleEnabled = createMemo(() => !!organizationSlug() && !!sessionQuery().data?.user);
  const tenantRoleQuery = useQuery(() => ({
    queryKey: ["member-role", organizationSlug(), sessionQuery().data?.user?.id],
    queryFn: async (): Promise<{ role: string | null }> => {
      const res = await organization.getActiveMemberRole({
        query: { organizationSlug: organizationSlug()! },
      });
      // Not a member (403) or unknown organization: settle with no role
      // rather than an error, so redirects can proceed
      if (res.error || !res.data) return { role: null };
      return { role: res.data.role };
    },
    enabled: tenantRoleEnabled(),
    staleTime: 1000 * 60,
  }));

  const role = createMemo(() =>
    organizationSlug() ? (tenantRoleQuery.data?.role ?? undefined) : activeMemberRoleQuery().data?.role
  );
  const userIsAdmin = createMemo(() => sessionQuery().data?.user.role === "admin");

  const canCreate = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "create"));
  const canList = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "list"));
  const canUpdate = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "update"));
  const canDelete = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "delete"));
  const canToggleSpotlight = createMemo(() => userIsAdmin() || checkProjectPermission(role(), "toggleSpotlight"));
  const canManageOrg = createMemo(() => userIsAdmin() || checkOrgPermission(role(), "update"));

  return {
    canCreate,
    canList,
    canUpdate,
    canDelete,
    canToggleSpotlight,
    canManageOrg,
    isPermissionsPending: createMemo(() => {
      if (sessionQuery().isPending) return true;
      if (organizationSlug()) return tenantRoleEnabled() && tenantRoleQuery.isPending;
      return activeMemberRoleQuery().isPending;
    }),
  };
}
